/**
 * Claude 對話訊息 block 渲染元件
 *
 * 把 stream-json 解析後的 blocks 陣列分型別渲染：
 *  - text: 一般段落（pre + 自動換行）
 *  - tool_use: 可摺疊 card，summary 顯示工具名 + 參數
 *  - tool_result: 灰底 code block；is_error=true 加紅框
 *  - thinking: 淡灰可摺疊
 */
import { useState } from 'react';

function preStyle(extra = {}) {
  return {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: '0.85rem',
    fontFamily: 'inherit',
    margin: 0,
    ...extra,
  };
}

function safeStringify(v) {
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch (_) {
    return String(v);
  }
}

function ToolUseBlock({ block, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded mb-2" style={{ background: 'rgba(13, 110, 253, 0.04)' }}>
      <button
        type="button"
        className="btn btn-link text-decoration-none p-2 w-100 text-start d-flex align-items-center"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{ fontSize: '0.85rem' }}
      >
        <i className={`bi bi-chevron-${open ? 'down' : 'right'} me-2`} aria-hidden="true"></i>
        <i className="bi bi-tools me-2" aria-hidden="true"></i>
        <span className="fw-semibold">{block.name || 'tool'}</span>
        {block.id && (
          <code className="ms-2 text-muted" style={{ fontSize: '0.7rem' }}>
            {block.id.slice(0, 12)}
          </code>
        )}
      </button>
      {open && (
        <div className="px-2 pb-2">
          <pre
            className="bg-body-tertiary border rounded p-2"
            style={preStyle({ fontSize: '0.78rem' })}
          >
            {safeStringify(block.input)}
          </pre>
        </div>
      )}
    </div>
  );
}

function ToolResultBlock({ block, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const text = safeStringify(block.content);
  const isError = block.is_error;
  const truncated = text.length > 4000 ? text.slice(0, 4000) + '\n…(後續已截斷)' : text;
  return (
    <div className={`border rounded mb-2 ${isError ? 'border-danger' : ''}`}>
      <button
        type="button"
        className="btn btn-link text-decoration-none p-2 w-100 text-start d-flex align-items-center"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{ fontSize: '0.85rem' }}
      >
        <i className={`bi bi-chevron-${open ? 'down' : 'right'} me-2`} aria-hidden="true"></i>
        <i
          className={`bi ${isError ? 'bi-exclamation-octagon text-danger' : 'bi-arrow-return-right text-muted'} me-2`}
          aria-hidden="true"
        ></i>
        <span className={`${isError ? 'text-danger' : 'text-muted'} fw-medium`}>
          {isError ? '工具錯誤' : '工具回傳'}
        </span>
        {block.tool_use_id && (
          <code className="ms-2 text-muted" style={{ fontSize: '0.7rem' }}>
            {block.tool_use_id.slice(0, 12)}
          </code>
        )}
      </button>
      {open && (
        <pre
          className={`bg-body-secondary border rounded p-2 mb-2 mx-2 ${isError ? 'border-danger' : ''}`}
          style={preStyle({ fontSize: '0.78rem', maxHeight: '320px', overflow: 'auto' })}
        >
          {truncated}
        </pre>
      )}
    </div>
  );
}

function ThinkingBlock({ block }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded mb-2" style={{ background: 'rgba(108, 117, 125, 0.06)' }}>
      <button
        type="button"
        className="btn btn-link text-decoration-none p-2 w-100 text-start text-muted"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{ fontSize: '0.8rem' }}
      >
        <i className={`bi bi-chevron-${open ? 'down' : 'right'} me-2`} aria-hidden="true"></i>
        <i className="bi bi-lightbulb me-2" aria-hidden="true"></i>思考中
      </button>
      {open && (
        <pre
          className="bg-body-tertiary border rounded p-2 mb-2 mx-2 text-muted"
          style={preStyle({ fontSize: '0.78rem' })}
        >
          {block.thinking}
        </pre>
      )}
    </div>
  );
}

function MetaRow({ meta }) {
  if (!meta) return null;
  const parts = [];
  if (meta.duration_ms != null) parts.push(`${(meta.duration_ms / 1000).toFixed(1)}s`);
  if (meta.num_turns != null) parts.push(`${meta.num_turns} turns`);
  if (meta.total_cost_usd != null) parts.push(`$${Number(meta.total_cost_usd).toFixed(4)}`);
  if (meta.exit_code != null && meta.exit_code !== 0) parts.push(`exit ${meta.exit_code}`);
  if (meta.interrupted) parts.push('已中斷');
  if (meta.rebuild?.message) parts.push(`rebuild: ${meta.rebuild.message}`);
  if (parts.length === 0) return null;
  return (
    <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
      {parts.join(' · ')}
    </div>
  );
}

export default function ClaudeMessageBlocks({ blocks, meta, streaming }) {
  if (!blocks || blocks.length === 0) {
    if (streaming) {
      return (
        <span className="text-muted" style={{ animation: 'blink 1s infinite' }}>
          ▋
        </span>
      );
    }
    return null;
  }

  return (
    <div>
      {blocks.map((block, idx) => {
        switch (block.type) {
          case 'text':
            return (
              <pre key={idx} style={preStyle({ marginBottom: '0.5rem' })}>
                {block.text}
                {streaming && idx === blocks.length - 1 && (
                  <span className="text-muted" style={{ animation: 'blink 1s infinite' }}>
                    ▋
                  </span>
                )}
              </pre>
            );
          case 'tool_use':
            return <ToolUseBlock key={idx} block={block} defaultOpen={false} />;
          case 'tool_result':
            return <ToolResultBlock key={idx} block={block} defaultOpen={!!block.is_error} />;
          case 'thinking':
            return <ThinkingBlock key={idx} block={block} />;
          default:
            return null;
        }
      })}
      <MetaRow meta={meta} />
    </div>
  );
}
